import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Maximize2, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "components/ui/dialog";
import { cn } from "lib/utils";

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 3;
const DEFAULT_STEP = 0.25;

function clampScale(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function ZoomControls({
  scale,
  onZoomIn,
  onZoomOut,
  onReset,
  minScale,
  maxScale,
  className,
  onPointerEventCapture,
}) {
  const stop = onPointerEventCapture;

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-md border border-border/80 bg-background/95 p-1 shadow-sm backdrop-blur-sm",
        className
      )}
      onClick={stop}
      onPointerDown={stop}
      onMouseDown={stop}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onZoomOut}
        disabled={scale <= minScale}
        aria-label="Zoom out"
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <span className="min-w-[2.75rem] text-center text-xs font-medium tabular-nums text-foreground">
        {Math.round(scale * 100)}%
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onZoomIn}
        disabled={scale >= maxScale}
        aria-label="Zoom in"
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onReset}
        disabled={scale === DEFAULT_MIN}
        aria-label="Reset zoom"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    </div>
  );
}

ZoomControls.propTypes = {
  scale: PropTypes.number.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  minScale: PropTypes.number.isRequired,
  maxScale: PropTypes.number.isRequired,
  className: PropTypes.string,
  onPointerEventCapture: PropTypes.func.isRequired,
};

/**
 * Image with zoom in/out controls. Use `variant="lightbox"` for small thumbnails;
 * use `zoomable={false}` for logos and decorative marks.
 */
export function ZoomableImage({
  src,
  alt = "",
  className,
  containerClassName,
  zoomable = true,
  variant = "inline",
  lightbox = true,
  minScale = DEFAULT_MIN,
  maxScale = DEFAULT_MAX,
  step = DEFAULT_STEP,
  onClick,
  ...imgProps
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxScale, setLightboxScale] = useState(DEFAULT_MIN);

  /** Prevent parent links, cards, and carousel controls from activating. */
  const stopInteraction = useCallback((event) => {
    event.stopPropagation();
    if (event.cancelable) {
      event.preventDefault();
    }
  }, []);

  const makeZoomHandlers = useCallback(
    (setValue) => ({
      zoomIn: () => setValue((current) => clampScale(current + step, minScale, maxScale)),
      zoomOut: () => setValue((current) => clampScale(current - step, minScale, maxScale)),
      reset: () => setValue(DEFAULT_MIN),
    }),
    [step, minScale, maxScale]
  );

  const lightboxZoom = makeZoomHandlers(setLightboxScale);

  const openLightbox = useCallback(
    (event, initialScale = DEFAULT_MIN) => {
      if (!lightbox || lightboxOpen) return;
      if (event) stopInteraction(event);
      setLightboxScale(initialScale);
      setLightboxOpen(true);
    },
    [lightbox, lightboxOpen, stopInteraction]
  );

  const openLightboxWithZoom = useCallback(
    (event, action) => {
      if (!lightbox) return;
      stopInteraction(event);
      setLightboxScale((current) => {
        const base = lightboxOpen ? current : DEFAULT_MIN;
        if (action === "in") return clampScale(base + step, minScale, maxScale);
        if (action === "out") return clampScale(base - step, minScale, maxScale);
        return DEFAULT_MIN;
      });
      setLightboxOpen(true);
    },
    [lightbox, lightboxOpen, step, minScale, maxScale, stopInteraction]
  );

  const handleContainerClick = useCallback(
    (event) => {
      stopInteraction(event);
      if (lightbox && !lightboxOpen) {
        openLightbox(event);
        return;
      }
      onClick?.(event);
    },
    [lightbox, lightboxOpen, openLightbox, stopInteraction, onClick]
  );

  const wrapZoomAction = useCallback(
    (action) => (event) => {
      stopInteraction(event);
      action();
    },
    [stopInteraction]
  );

  if (!src || !zoomable) {
    if (!src) return null;
    return <img src={src} alt={alt} className={className} onClick={onClick} {...imgProps} />;
  }

  const showInlineControls = variant === "inline" && lightbox;
  const isLightboxTrigger = variant === "lightbox" && lightbox;

  return (
    <>
    <div
      role="presentation"
      className={cn(
        "group/zoom relative h-full w-full overflow-hidden",
        lightbox && !lightboxOpen && "cursor-zoom-in",
        containerClassName
      )}
      onClick={handleContainerClick}
      onPointerDown={stopInteraction}
      onMouseDown={stopInteraction}
    >
        <img
          src={src}
          alt={alt}
          className={cn("h-full w-full object-cover", className)}
          draggable={false}
          {...imgProps}
        />

        {showInlineControls ? (
          <div
            className={cn(
              "absolute bottom-2 right-2 z-10 flex items-center gap-1",
              "opacity-0 transition-opacity group-hover/zoom:opacity-100 focus-within:opacity-100"
            )}
            onClick={stopInteraction}
            onPointerDown={stopInteraction}
            onMouseDown={stopInteraction}
          >
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              className="h-8 w-8 shrink-0 shadow-sm"
              onClick={(event) => openLightbox(event)}
              aria-label="View full size"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <ZoomControls
              scale={lightboxScale}
              onZoomIn={(event) => openLightboxWithZoom(event, "in")}
              onZoomOut={(event) => openLightboxWithZoom(event, "out")}
              onReset={(event) => openLightbox(event)}
              minScale={minScale}
              maxScale={maxScale}
              onPointerEventCapture={stopInteraction}
            />
          </div>
        ) : null}

        {isLightboxTrigger ? (
          <span className="pointer-events-none absolute bottom-2 right-2 z-10 rounded-md bg-background/90 p-1.5 opacity-0 shadow-sm transition-opacity group-hover/zoom:opacity-100">
            <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </span>
        ) : null}
    </div>

    {lightbox ? (
      <Dialog
        open={lightboxOpen}
        onOpenChange={(open) => {
          setLightboxOpen(open);
          if (!open) setLightboxScale(DEFAULT_MIN);
        }}
      >
        <DialogContent
          showCloseButton
          className={cn(
            "fixed inset-0 z-50 flex h-dvh w-screen max-w-none flex-col gap-0",
            "translate-x-0 translate-y-0 rounded-none border-0 bg-black/95 p-0 shadow-none",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "sm:max-w-none",
            "[&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-4",
            "[&_[data-slot=dialog-close]]:z-[60] [&_[data-slot=dialog-close]]:text-white",
            "[&_[data-slot=dialog-close]]:opacity-90",
            "[&_[data-slot=dialog-close]]:hover:bg-white/15 [&_[data-slot=dialog-close]]:hover:opacity-100"
          )}
        >
          <DialogTitle className="sr-only">{alt || "Image preview"}</DialogTitle>
          <DialogDescription className="sr-only">
            Zoom in or out using the controls below.
          </DialogDescription>

          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6 pb-24">
            <img
              src={src}
              alt={alt}
              className="mx-auto max-h-[calc(100dvh-8rem)] max-w-[min(100%,calc(100vw-3rem))] object-contain transition-transform duration-200 ease-out"
              style={{
                transform: `scale(${lightboxScale})`,
                transformOrigin: "center center",
              }}
              draggable={false}
            />
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-8">
            <div className="pointer-events-auto">
              <ZoomControls
                scale={lightboxScale}
                onZoomIn={wrapZoomAction(lightboxZoom.zoomIn)}
                onZoomOut={wrapZoomAction(lightboxZoom.zoomOut)}
                onReset={wrapZoomAction(lightboxZoom.reset)}
                minScale={minScale}
                maxScale={maxScale}
                onPointerEventCapture={(event) => event.stopPropagation()}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    ) : null}
    </>
  );
}

ZoomableImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  zoomable: PropTypes.bool,
  /** `inline` — hover zoom controls; `lightbox` — click to open zoom dialog */
  variant: PropTypes.oneOf(["inline", "lightbox"]),
  lightbox: PropTypes.bool,
  minScale: PropTypes.number,
  maxScale: PropTypes.number,
  step: PropTypes.number,
  onClick: PropTypes.func,
};
